import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/authContext';
import { useSharePointAuth } from '../contexts/sharePointAuthContext';
import { useSharePoint } from '../hooks/useSharePoint';
export const SharePointDocumentPicker = ({ onSelect, onClose, isOpen })=>{
    const { user } = useAuth();
    const { isSharePointConnected, connectSharePoint } = useSharePointAuth();
    const { getUserSiteAccess, listSiteDrives, listDriveItems, checkDocumentPermission } = useSharePoint();
    const [sites, setSites] = useState([]);
    const [drives, setDrives] = useState([]);
    const [items, setItems] = useState([]);
    const [view, setView] = useState('sites');
    const [selectedSiteId, setSelectedSiteId] = useState(null);
    const [selectedSiteName, setSelectedSiteName] = useState('');
    const [selectedDriveId, setSelectedDriveId] = useState(null);
    const [folderStack, setFolderStack] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    useEffect(()=>{
        if (!isOpen) return;
        const handleKeyDown = (event)=>{
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return ()=>document.removeEventListener('keydown', handleKeyDown);
    }, [
        isOpen,
        onClose
    ]);
    // Load user's accessible sites
    useEffect(()=>{
        if (!isOpen || !isSharePointConnected || !user) return;
        const userId = user.id;
        const tenantId = user.tenant_id;
        if (!userId || !tenantId) return;
        const loadSites = async ()=>{
            try {
                setLoading(true);
                setError(null);
                const access = await getUserSiteAccess(userId, tenantId);
                if (access?.sites) {
                    setSites(access.sites);
                }
            } catch (err) {
                setError(`Failed to load SharePoint sites: ${err instanceof Error ? err.message : 'Unknown error'}`);
            } finally{
                setLoading(false);
            }
        };
        loadSites();
    }, [
        isOpen,
        isSharePointConnected,
        user,
        getUserSiteAccess
    ]);
    // Reset state when picker closes
    useEffect(()=>{
        if (!isOpen) {
            setView('sites');
            setSelectedSiteId(null);
            setSelectedSiteName('');
            setSelectedDriveId(null);
            setFolderStack([]);
            setDrives([]);
            setItems([]);
            setSearchQuery('');
            setError(null);
        }
    }, [
        isOpen
    ]);
    const handleSiteClick = useCallback(async (siteId, siteName)=>{
        try {
            setLoading(true);
            setError(null);
            setSelectedSiteId(siteId);
            setSelectedSiteName(siteName);
            const result = await listSiteDrives(siteId);
            if (result) {
                setDrives(result);
                setView('drives');
            }
        } catch (err) {
            setError(`Failed to load drives: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally{
            setLoading(false);
        }
    }, [
        listSiteDrives
    ]);
    const handleDriveClick = useCallback(async (driveId)=>{
        if (!selectedSiteId) return;
        try {
            setLoading(true);
            setError(null);
            setSelectedDriveId(driveId);
            setFolderStack([]);
            const result = await listDriveItems(selectedSiteId, driveId);
            if (result?.items) {
                setItems(result.items);
                setView('items');
            }
        } catch (err) {
            setError(`Failed to load items: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally{
            setLoading(false);
        }
    }, [
        selectedSiteId,
        listDriveItems
    ]);
    const handleFolderClick = useCallback(async (folderId, folderName)=>{
        if (!selectedSiteId || !selectedDriveId) return;
        try {
            setLoading(true);
            setError(null);
            setFolderStack((prev)=>[
                    ...prev,
                    {
                        id: folderId,
                        name: folderName
                    }
                ]);
            const result = await listDriveItems(selectedSiteId, selectedDriveId, folderId);
            if (result?.items) {
                setItems(result.items);
            }
        } catch (err) {
            setError(`Failed to load folder: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally{
            setLoading(false);
        }
    }, [
        selectedSiteId,
        selectedDriveId,
        listDriveItems
    ]);
    const handleBack = useCallback(async ()=>{
        if (view === 'items' && folderStack.length > 0) {
            // Go up one folder level
            const newStack = folderStack.slice(0, -1);
            setFolderStack(newStack);
            if (!selectedSiteId || !selectedDriveId) return;
            setLoading(true);
            const parentFolderId = newStack.length > 0 ? newStack[newStack.length - 1].id : undefined;
            const result = await listDriveItems(selectedSiteId, selectedDriveId, parentFolderId);
            if (result?.items) setItems(result.items);
            setLoading(false);
        } else if (view === 'items') {
            // Go back to drives
            setView('drives');
            setSelectedDriveId(null);
            setItems([]);
        } else if (view === 'drives') {
            // Go back to sites
            setView('sites');
            setSelectedSiteId(null);
            setSelectedSiteName('');
            setDrives([]);
        }
    }, [
        view,
        folderStack,
        selectedSiteId,
        selectedDriveId,
        listDriveItems
    ]);
    const handleDocumentSelect = useCallback(async (item)=>{
        try {
            setLoading(true);
            setError(null);
            if (!user?.id || !user?.tenant_id) {
                setError('User information missing');
                return;
            }
            const permission = await checkDocumentPermission(user.id, user.tenant_id, item.resource_id);
            if (!permission) {
                setError('Failed to check document permissions. Please try again.');
                return;
            }
            if (!permission.allowed) {
                setError(`Access denied: ${permission.reason || 'You do not have permission to access this document'}`);
                return;
            }
            const siteName = selectedSiteName || sites.find((s)=>s.site_id === selectedSiteId)?.site_name || '';
            const siteUrl = sites.find((s)=>s.site_id === selectedSiteId)?.site_url || '';
            await onSelect({
                id: item.id,
                title: item.name,
                resource_id: item.resource_id,
                site_id: item.site_id,
                site_name: siteName,
                site_url: siteUrl,
                lastModified: item.modified_at,
                size: item.size
            });
            onClose();
        } catch (err) {
            setError(`Failed to select document: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally{
            setLoading(false);
        }
    }, [
        user,
        checkDocumentPermission,
        onSelect,
        onClose,
        selectedSiteId,
        selectedSiteName,
        sites
    ]);
    const filteredSites = sites.filter((site)=>site.site_name.toLowerCase().includes(searchQuery.toLowerCase()) || site.site_url.toLowerCase().includes(searchQuery.toLowerCase()));
    const formatFileSize = (bytes)=>{
        const sizes = [
            'Bytes',
            'KB',
            'MB',
            'GB'
        ];
        if (bytes === 0) return '0 Bytes';
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${Math.round(bytes / 1024 ** i * 100) / 100} ${sizes[i]}`;
    };
    if (!isOpen) return null;
    if (!isSharePointConnected) {
        return /*#__PURE__*/ _jsx("div", {
            className: "fixed inset-0 z-[1000] flex items-center justify-center bg-black/50",
            onClick: onClose,
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col w-[90%] max-w-[600px] overflow-hidden bg-white rounded-lg shadow-lg",
                onClick: (e)=>e.stopPropagation(),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between gap-4 px-5 py-5 border-b border-gray-200",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold text-gray-900",
                                children: "Connect SharePoint"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                onClick: onClose,
                                className: "flex items-center justify-center w-8 h-8 text-2xl text-gray-500 rounded hover:bg-gray-100 transition-colors",
                                children: "×"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col items-center justify-center gap-4 px-5 py-12",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-gray-600 text-center",
                                children: "Connect your SharePoint account to select documents directly from your sites."
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>{
                                    void connectSharePoint();
                                },
                                className: "px-6 py-2 bg-blue-600 text-white rounded font-medium text-sm hover:bg-blue-700 transition-colors",
                                children: "Connect to SharePoint"
                            })
                        ]
                    })
                ]
            })
        });
    }
    const breadcrumbLabel = view === 'sites' ? 'Sites' : view === 'drives' ? selectedSiteName : folderStack.length > 0 ? folderStack[folderStack.length - 1].name : 'Files';
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 z-[1000] flex items-center justify-center bg-black/50",
        onClick: onClose,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col w-[90%] max-w-[600px] max-h-[80vh] overflow-hidden bg-white rounded-lg shadow-lg",
            onClick: (e)=>e.stopPropagation(),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between gap-4 px-5 py-5 border-b border-gray-200",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-xl font-semibold text-gray-900",
                            children: "Select SharePoint Document"
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            onClick: onClose,
                            className: "flex items-center justify-center w-8 h-8 text-2xl text-gray-500 rounded hover:bg-gray-100 transition-colors",
                            children: "×"
                        })
                    ]
                }),
                error && /*#__PURE__*/ _jsx("div", {
                    className: "mx-4 mt-4 p-3 bg-red-100 border border-red-300 text-red-900 rounded",
                    children: /*#__PURE__*/ _jsx("p", {
                        className: "text-sm m-0",
                        children: error
                    })
                }),
                view !== 'sites' && /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-3 px-4 py-3 border-b border-gray-200 text-sm text-gray-700",
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            onClick: handleBack,
                            className: "text-blue-500 hover:bg-blue-50 px-2 py-1 rounded transition-colors",
                            children: "← Back"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: breadcrumbLabel
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col flex-1 overflow-y-auto",
                    children: [
                        view === 'sites' && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "px-4 py-4 border-b border-gray-200",
                                    children: /*#__PURE__*/ _jsx("input", {
                                        type: "text",
                                        placeholder: "Search sites...",
                                        value: searchQuery,
                                        onChange: (e)=>setSearchQuery(e.target.value),
                                        disabled: loading,
                                        className: "w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-100 disabled:bg-gray-50 disabled:text-gray-600"
                                    })
                                }),
                                loading ? /*#__PURE__*/ _jsx("div", {
                                    className: "flex items-center justify-center h-[200px] text-gray-500 text-sm",
                                    children: "Loading sites..."
                                }) : sites.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col items-center justify-center h-[200px] text-gray-500 text-center px-5",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm",
                                            children: "No SharePoint sites found."
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-gray-400 mt-2",
                                            children: "Make sure you're connected to SharePoint in Settings."
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsx("div", {
                                    className: "flex-1 overflow-y-auto",
                                    children: filteredSites.map((site)=>/*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors",
                                            onClick: ()=>handleSiteClick(site.site_id, site.site_name),
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-2xl flex-shrink-0",
                                                    children: "📁"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex-1 min-w-0",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("h3", {
                                                            className: "text-sm font-medium text-gray-900 truncate",
                                                            children: site.site_name
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-xs text-gray-500 truncate",
                                                            children: site.site_url
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-gray-300 text-base flex-shrink-0",
                                                    children: "→"
                                                })
                                            ]
                                        }, site.site_id))
                                })
                            ]
                        }),
                        view === 'drives' && (loading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center h-[200px] text-gray-500 text-sm",
                            children: "Loading document libraries..."
                        }) : drives.length === 0 ? /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-col items-center justify-center h-[200px] text-gray-500 text-center px-5",
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-sm",
                                children: "No document libraries found in this site."
                            })
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "flex-1 overflow-y-auto",
                            children: drives.map((drive)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors",
                                    onClick: ()=>handleDriveClick(drive.id),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-2xl flex-shrink-0",
                                            children: "📚"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex-1 min-w-0",
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "text-sm font-medium text-gray-900 truncate",
                                                    children: drive.name
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-xs text-gray-500",
                                                    children: drive.drive_type
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-gray-300 text-base flex-shrink-0",
                                            children: "→"
                                        })
                                    ]
                                }, drive.id))
                        })),
                        view === 'items' && (loading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center h-[200px] text-gray-500 text-sm",
                            children: "Loading..."
                        }) : items.length === 0 ? /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-col items-center justify-center h-[200px] text-gray-500 text-center px-5",
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-sm",
                                children: "This folder is empty."
                            })
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "flex-1 overflow-y-auto",
                            children: items.sort((a, b)=>{
                                if (a.is_folder && !b.is_folder) return -1;
                                if (!a.is_folder && b.is_folder) return 1;
                                return a.name.localeCompare(b.name);
                            }).map((item)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors",
                                    onClick: ()=>item.is_folder ? handleFolderClick(item.id, item.name) : handleDocumentSelect(item),
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-2xl flex-shrink-0",
                                            children: item.is_folder ? '📁' : '📄'
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex-1 min-w-0",
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "text-sm font-medium text-gray-900 truncate",
                                                    children: item.name
                                                }),
                                                /*#__PURE__*/ _jsxs("p", {
                                                    className: "text-xs text-gray-500 mt-0.5",
                                                    children: [
                                                        item.is_folder ? `${item.child_count} items` : formatFileSize(item.size),
                                                        item.modified_at && ` · ${new Date(item.modified_at).toLocaleDateString()}`
                                                    ]
                                                })
                                            ]
                                        }),
                                        item.is_folder && /*#__PURE__*/ _jsx("div", {
                                            className: "text-gray-300 text-base flex-shrink-0",
                                            children: "→"
                                        })
                                    ]
                                }, item.id))
                        }))
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50",
                    children: /*#__PURE__*/ _jsx("button", {
                        onClick: onClose,
                        disabled: loading,
                        className: "px-4 py-2 bg-white border border-gray-300 rounded font-medium text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
                        children: "Cancel"
                    })
                })
            ]
        })
    });
};
